import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  LockReset as LockResetIcon
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * ChangePasswordForm — self-service password change for any authenticated user.
 *
 * Shared by the customer Profile page, the maid portal profile panel, and the
 * admin profile. Calls POST /api/auth/change-password which verifies the
 * current password server-side.
 */
const ChangePasswordForm = () => {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { severity, text }

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const toggleShow = (field) => () => setShow((s) => ({ ...s, [field]: !s[field] }));

  const clientError = () => {
    if (!form.current) return 'Please enter your current password.';
    if (form.next.length < 6) return 'New password must be at least 6 characters long.';
    if (form.next === form.current) return 'New password must be different from your current password.';
    if (form.next !== form.confirm) return 'New password and confirmation do not match.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = clientError();
    if (error) {
      setMessage({ severity: 'error', text: error });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          currentPassword: form.current,
          newPassword: form.next
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to change password.');
      }

      setForm({ current: '', next: '', confirm: '' });
      setMessage({ severity: 'success', text: data.message || 'Your password has been changed.' });
    } catch (err) {
      setMessage({ severity: 'error', text: err.message || 'Failed to change password.' });
    } finally {
      setSaving(false);
    }
  };

  const passwordField = (label, field, autoComplete) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      type={show[field] ? 'text' : 'password'}
      value={form[field]}
      onChange={setField(field)}
      autoComplete={autoComplete}
      disabled={saving}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={toggleShow(field)}
              edge="end"
              size="small"
              aria-label={show[field] ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
            >
              {show[field] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
          </InputAdornment>
        )
      }}
    />
  );

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Stack spacing={2}>
        {passwordField('Current password', 'current', 'current-password')}
        {passwordField('New password', 'next', 'new-password')}
        {passwordField('Confirm new password', 'confirm', 'new-password')}
      </Stack>

      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          type="submit"
          variant="contained"
          disabled={saving || !form.current || !form.next || !form.confirm}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <LockResetIcon />}
        >
          {saving ? 'Changing…' : 'Change Password'}
        </Button>
      </Box>
    </Box>
  );
};

export default ChangePasswordForm;
